import React, { useState, useEffect } from 'react';
import contextAnalyticsService from '../../../services/contextAnalyticsService';
import {
  ChatBubbleLeftIcon,
  PhoneIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';

const InteractionTimeline = ({ dateRange, selectedAgent, showNotification }) => {
  const [loading, setLoading] = useState(true);
  const [interactions, setInteractions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const LIMIT = 30;

  useEffect(() => {
    setPage(0);
    setInteractions([]);
    fetchInteractions(0);
  }, [dateRange, selectedAgent]);

  const fetchInteractions = async (pageNum) => {
    try {
      if (pageNum === 0) setLoading(true);
      else setLoadingMore(true);

      const filters = buildFilters();
      filters.limit = LIMIT;
      filters.skip = pageNum * LIMIT;

      const requests = [
        contextAnalyticsService.getInteractionTimeline(filters)
      ];
      // Only fetch summary on first page
      if (pageNum === 0) {
        requests.push(contextAnalyticsService.getInteractionsSummary(buildFilters()));
      }

      const responses = await Promise.all(requests);
      const timelineResponse = responses[0];
      const summaryResponse = responses[1];

      if (timelineResponse.success) {
        const newData = timelineResponse.data || [];
        if (pageNum === 0) {
          setInteractions(newData);
        } else {
          setInteractions(prev => [...prev, ...newData]);
        }
        setHasMore(newData.length >= LIMIT);
      }

      if (summaryResponse?.success) {
        setSummary(summaryResponse.data);
      }
    } catch (error) {
      logger.error('Error fetching interactions:', error);
      showNotification?.('Failed to load interactions', 'error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchInteractions(nextPage);
  };

  const buildFilters = () => {
    const filters = {};
    const now = Date.now();

    switch (dateRange) {
      case 'today': {
        const today = new Date(now);
        today.setHours(0, 0, 0, 0);
        filters.startDate = today.toISOString();
        break;
      }
      case 'week':
        filters.startDate = new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case 'month':
        filters.startDate = new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString();
        break;
      default:
        break;
    }

    if (selectedAgent) {
      filters.agentId = selectedAgent;
    }

    return filters;
  };

  const getChannelIcon = (channel) => {
    const icons = {
      email: EnvelopeIcon,
      voice: PhoneIcon,
      sms: DevicePhoneMobileIcon,
      chat: ChatBubbleLeftIcon
    };
    return icons[channel] || ChatBubbleLeftIcon;
  };

  const getChannelColor = (channel) => {
    const colors = {
      email: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      voice: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      sms: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      chat: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
    };
    return colors[channel] || 'bg-gray-100 text-gray-800';
  };

  const getSentimentColor = (score) => {
    if (score > 0.3) return 'text-green-600 dark:text-green-400';
    if (score < -0.3) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {summary.byChannel?.map((channelData) => {
            const Icon = getChannelIcon(channelData._id);
            return (
              <div key={channelData._id} className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <Icon className="h-8 w-8 text-gray-400" />
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">
                    {channelData.count}
                  </span>
                </div>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 capitalize">
                  {channelData._id} Interactions
                </p>
                {channelData.avgDuration && (
                  <p className="mt-1 text-xs text-gray-400">
                    Avg: {Math.round(channelData.avgDuration / 1000)}s
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Interactions Timeline */}
      <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Recent Interactions
            </h3>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {interactions.length} loaded{hasMore ? ' (more available)' : ''}
            </span>
          </div>
        </div>

        <div className="p-6">
          {interactions && interactions.length > 0 ? (
            <div className="flow-root">
              <ul className="-mb-8">
                {interactions.map((interaction, idx) => {
                  const Icon = getChannelIcon(interaction.channel);
                  return (
                    <li key={interaction._id}>
                      <div className="relative pb-8">
                        {idx !== interactions.length - 1 && (
                          <span
                            className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200 dark:bg-[#222222]"
                            aria-hidden="true"
                          />
                        )}
                        <div className="relative flex space-x-3">
                          <div>
                            <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white dark:ring-[#1a1a1a] ${getChannelColor(interaction.channel)}`}>
                              <Icon className="h-5 w-5" />
                            </span>
                          </div>
                          <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                            <div className="flex-1">
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {interaction.agentId?.agentName || 'Unknown Agent'}
                                </span>
                                {' '}handled{' '}
                                <span className="font-medium">{interaction.direction}</span>
                                {' '}{interaction.channel}
                                {' '}from{' '}
                                <span className="font-medium">{interaction.contact?.name || interaction.contact?.email || 'Unknown'}</span>
                              </p>
                              <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                                {interaction.sentiment?.score && (
                                  <span className={`font-medium ${getSentimentColor(interaction.sentiment.score)}`}>
                                    Sentiment: {(interaction.sentiment.score * 100).toFixed(0)}%
                                  </span>
                                )}
                                {interaction.outcome && (
                                  <span className="px-2 py-1 rounded-full bg-gray-100 dark:bg-[#222222]">
                                    {interaction.outcome}
                                  </span>
                                )}
                                {interaction.duration && (
                                  <span>Duration: {Math.round(interaction.duration / 1000)}s</span>
                                )}
                                {interaction.costUSD && (
                                  <span>Cost: ${interaction.costUSD.toFixed(4)}</span>
                                )}
                              </div>
                              {interaction.intents && interaction.intents.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {interaction.intents.map((intent, i) => (
                                    <span
                                      key={i}
                                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                                    >
                                      {intent}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="whitespace-nowrap text-right text-sm text-gray-500 dark:text-gray-400">
                              <time dateTime={interaction.createdAt}>
                                {new Date(interaction.createdAt).toLocaleString()}
                              </time>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {/* Load More Button */}
              {hasMore && (
                <div className="mt-6 text-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-[#222222] hover:bg-gray-50 dark:hover:bg-[#2a2a2a] disabled:opacity-50"
                  >
                    {loadingMore ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                        Loading...
                      </>
                    ) : (
                      'Load More'
                    )}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">
              No interactions found for the selected period.
            </p>
          )}
        </div>
      </div>

      {/* Outcome Breakdown */}
      {summary?.outcomeBreakdown && summary.outcomeBreakdown.length > 0 && (
        <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow p-6">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
            Outcome Breakdown
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {summary.outcomeBreakdown.map((outcome) => (
              <div key={outcome._id} className="text-center">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {outcome.count}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                  {outcome._id?.replace(/_/g, ' ')}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractionTimeline;
